import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from '@mui/material'
import {
  GridColDef,
  GridFilterModel,
  GridFilterItem,
} from '@mui/x-data-grid'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

import { TreasuryDataGrid } from '../DataGrid'
import { FloatingActionToolbar } from '../Common'



// Define interfaces for the data structures
interface FacturaItem {
  id: string
  'No. CONS.': string
  LOTE: string
  'No.': string
  NIT: string
  PROVEEDOR: string
  TIPO: string
  CONCEPTO: string
  FECHA: string
  'No. DOC.': string
  'SUBTOTAL': number
  'V/R IVA': number
  'PREST.': number
  'V/R COBRO': number
  'DCTO FIN.': number
  'RET FTE': number
  'RET IVA': number
  'RET ICA': number
  'RETENIDO': number
  'L. ANTI': number
  'PREST. EMPLEADO': number
  'DCTO NOM EMP.': number
  'V/R CHEQUE': number
  'CLASIFIC.': string
}

interface EquivalenciaItem {
  id: string
  'Codigo Gerencia': string
  Concepto: string
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

export interface ActualizarFacturasProps {
  data: {
    facturas: FacturaItem[]
    equivalencias_conceptos: EquivalenciaItem[]
  }
  mensaje: string
  jobId?: string
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      id={`payment-tabpanel-${index}`}
      aria-labelledby={`payment-tab-${index}`}
      style={{
        position: value === index ? 'relative' : 'absolute',
        top: value === index ? 'auto' : '-9999px',
        left: value === index ? 'auto' : '-9999px',
        width: '100%',
        minHeight: '400px',
        visibility: value === index ? 'visible' : 'hidden',
        zIndex: value === index ? 1 : -1,
      }}
      {...other}
    >
      <Box sx={{ pt: 3 }}>{children}</Box>
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `payment-tab-${index}`,
    'aria-controls': `payment-tabpanel-${index}`,
  }
}

// Manual filter operators that replicate MUI DataGrid logic
const filterOperators = {
  contains: (cellValue: any, filterValue: any) => 
    String(cellValue || '').toLowerCase().includes(String(filterValue || '').toLowerCase()),
  equals: (cellValue: any, filterValue: any) => {
    // Handle Date objects for date columns
    if (cellValue instanceof Date && filterValue instanceof Date) {
      return cellValue.getTime() === filterValue.getTime()
    }
    return cellValue === filterValue
  },
  startsWith: (cellValue: any, filterValue: any) => 
    String(cellValue || '').toLowerCase().startsWith(String(filterValue || '').toLowerCase()),
  endsWith: (cellValue: any, filterValue: any) => 
    String(cellValue || '').toLowerCase().endsWith(String(filterValue || '').toLowerCase()),
  isEmpty: (cellValue: any) => cellValue === '' || cellValue === null || cellValue === undefined,
  isNotEmpty: (cellValue: any) => cellValue !== '' && cellValue !== null && cellValue !== undefined,
  '>': (cellValue: any, filterValue: any) => {
    // Handle Date objects for date columns
    if (cellValue instanceof Date && filterValue instanceof Date) {
      return cellValue.getTime() > filterValue.getTime()
    }
    return Number(cellValue) > Number(filterValue)
  },
  '>=': (cellValue: any, filterValue: any) => {
    // Handle Date objects for date columns
    if (cellValue instanceof Date && filterValue instanceof Date) {
      return cellValue.getTime() >= filterValue.getTime()
    }
    return Number(cellValue) >= Number(filterValue)
  },
  '<': (cellValue: any, filterValue: any) => {
    // Handle Date objects for date columns
    if (cellValue instanceof Date && filterValue instanceof Date) {
      return cellValue.getTime() < filterValue.getTime()
    }
    return Number(cellValue) < Number(filterValue)
  },
  '<=': (cellValue: any, filterValue: any) => {
    // Handle Date objects for date columns
    if (cellValue instanceof Date && filterValue instanceof Date) {
      return cellValue.getTime() <= filterValue.getTime()
    }
    return Number(cellValue) <= Number(filterValue)
  },
  // Date-specific operators
  is: (cellValue: any, filterValue: any) => {
    // Handle Date objects for date columns
    if (cellValue instanceof Date && filterValue instanceof Date) {
      return cellValue.getTime() === filterValue.getTime()
    }
    return cellValue === filterValue
  },
  not: (cellValue: any, filterValue: any) => {
    // Handle Date objects for date columns
    if (cellValue instanceof Date && filterValue instanceof Date) {
      return cellValue.getTime() !== filterValue.getTime()
    }
    return cellValue !== filterValue
  },
  after: (cellValue: any, filterValue: any) => {
    // Handle Date objects for date columns
    if (cellValue instanceof Date && filterValue instanceof Date) {
      return cellValue.getTime() > filterValue.getTime()
    }
    return Number(cellValue) > Number(filterValue)
  },
  onOrAfter: (cellValue: any, filterValue: any) => {
    // Handle Date objects for date columns
    if (cellValue instanceof Date && filterValue instanceof Date) {
      return cellValue.getTime() >= filterValue.getTime()
    }
    return Number(cellValue) >= Number(filterValue)
  },
  before: (cellValue: any, filterValue: any) => {
    // Handle Date objects for date columns
    if (cellValue instanceof Date && filterValue instanceof Date) {
      return cellValue.getTime() < filterValue.getTime()
    }
    return Number(cellValue) < Number(filterValue)
  },
  onOrBefore: (cellValue: any, filterValue: any) => {
    // Handle Date objects for date columns
    if (cellValue instanceof Date && filterValue instanceof Date) {
      return cellValue.getTime() <= filterValue.getTime()
    }
    return Number(cellValue) <= Number(filterValue)
  },
}

// Manual filtering function that replicates DataGrid's exact logic without timezone issues
const applyFilterToData = (data: any[], filterModel: GridFilterModel, columns: GridColDef[]): any[] => {
  if (!data || data.length === 0) return data
  
  let filtered = [...data]
  
  // Apply column filters
  if (filterModel.items && filterModel.items.length > 0) {
    filtered = filtered.filter(row => {
      return filterModel.items!.every(filterItem => {
        if (!filterItem.field || !filterItem.operator || filterItem.value === undefined) {
          return true
        }
        
        // Find the column definition to check for type
        const columnDef = columns.find(col => col.field === filterItem.field)
        if (!columnDef) return true
        
        // Get the raw cell value (no valueGetter transformation for filtering)
        const cellValue = row[filterItem.field]
        const filterValue = filterItem.value
        const operator = filterOperators[filterItem.operator as keyof typeof filterOperators]
        
        if (operator) {
          return operator(cellValue, filterValue)
        }
        
        return true
      })
    })
  }
  
  // Apply quick filter - all words must match (AND logic, same as MUI DataGrid)
  if (filterModel.quickFilterValues && filterModel.quickFilterValues.length > 0) {
    filtered = filtered.filter(row => {
      // Build row text from raw values (no transformations for quick filter)
      const rowValues = Object.values(row).filter(val => val !== undefined && val !== null)
      const rowText = rowValues.join(' ').toLowerCase()
      
      // All quick filter values must be found in the row (AND logic)
      return filterModel.quickFilterValues!.every(filterValue => 
        rowText.includes(String(filterValue || '').toLowerCase())
      )
    })
  }
  
  return filtered
}

// Main component with centralized filter state
const ActualizarFacturasInner: React.FC<ActualizarFacturasProps> = ({
  data,
  mensaje,
  jobId,
}) => {
  // Original data tables (read-only from props)
  const originalData = useMemo(() => data, [data])

  // Use original data directly since no selection/moving is needed
  const tableData = useMemo(() => ({
    facturas: originalData.facturas,
    equivalencias_conceptos: originalData.equivalencias_conceptos,
  }), [originalData])

  // Centralized filter state
  const [globalFilterModel, setGlobalFilterModel] = useState<GridFilterModel>({
    items: [],
    quickFilterValues: []
  })

  // Handle filter changes from any DataGrid
  const handleGlobalFilterChange = useCallback((newFilterModel: GridFilterModel) => {
    console.log('🌐 CENTRAL FILTER UPDATE:', newFilterModel)
    setGlobalFilterModel(newFilterModel)
  }, [])

  // Extract unique values for singleSelect columns
  const uniqueValues = useMemo(() => {
    const allData = [
      ...tableData.facturas,
      ...tableData.equivalencias_conceptos,
    ];

    const getUniqueValues = (field: string) => {
      const values = allData
        .map(item => item[field])
        .filter(value => value != null && value !== '')
        .filter((value, index, self) => self.indexOf(value) === index)
        .sort();
      return values;
    };

    return {
      clasificacion: getUniqueValues('CLASIFIC.'),
    };
  }, [tableData]);

  // Column definitions for filtering
  const facturaColumns: GridColDef[] = useMemo(() => [
    { field: 'No. CONS.', filterable: true },
    { field: 'LOTE', filterable: true },
    { field: 'No.', filterable: true },
    { field: 'NIT', filterable: true },
    { field: 'PROVEEDOR', filterable: true },
    { field: 'TIPO', filterable: true },
    { field: 'CONCEPTO', filterable: true },
    { field: 'FECHA', filterable: true, type: 'date' },
    { field: 'No. DOC.', filterable: true },
    { field: 'SUBTOTAL', filterable: true },
    { field: 'V/R IVA', filterable: true },
    { field: 'PREST.', filterable: true },
    { field: 'V/R COBRO', filterable: true },
    { field: 'DCTO FIN.', filterable: true },
    { field: 'RET FTE', filterable: true },
    { field: 'RET IVA', filterable: true },
    { field: 'RET ICA', filterable: true },
    { field: 'RETENIDO', filterable: true },
    { field: 'L. ANTI', filterable: true },
    { field: 'PREST. EMPLEADO', filterable: true },
    { field: 'DCTO NOM EMP.', filterable: true },
    { field: 'V/R CHEQUE', filterable: true },
    { field: 'CLASIFIC.', filterable: true, type: 'singleSelect', valueOptions: uniqueValues.clasificacion },
  ], [uniqueValues])

  const equivalenciasColumnsForFiltering: GridColDef[] = useMemo(() => [
    { field: 'Codigo Gerencia', filterable: true},
    { field: 'Concepto', filterable: true },
  ], [uniqueValues])

  // Calculate filtered data for all tables manually - this updates immediately!
  const filteredTableData = useMemo(() => {
    const result = {
      facturas: applyFilterToData(tableData.facturas, globalFilterModel, facturaColumns),
      equivalencias_conceptos: applyFilterToData(tableData.equivalencias_conceptos, globalFilterModel, facturaColumns),
    }
    
    console.log('📊 MANUAL FILTER RESULTS:', {
      facturas: `${result.facturas.length}/${tableData.facturas.length}`,
      equivalencias_conceptos: `${result.equivalencias_conceptos.length}/${tableData.equivalencias_conceptos.length}`
    })
    
    return result
  }, [tableData, globalFilterModel, facturaColumns, equivalenciasColumnsForFiltering])

  // Track current tab (local state only)
  const [currentTab, setCurrentTab] = useState(0)
  
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  




  // Format currency for Colombian pesos
  const formatCurrency = useCallback((value: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(value)
  }, [])



  // Calculate statistics based on manually filtered data - updates immediately!
  const statistics = useMemo(() => {
    const totalProgramado = filteredTableData.facturas?.reduce((sum, item) => sum + (item['V/R CHEQUE'] || 0), 0) || 0
    
    // Calculate saldo disponible total only for distinct 'No. Cuenta' values
    const saldoDisponibleTotal = (() => {
      if (!filteredTableData.saldos_restantes || filteredTableData.saldos_restantes.length === 0) {
        return 0
      }
      
      // Group by No. Cuenta and keep only one record per unique account
      const uniqueAccounts = new Map<string, EquivalenciaItem>()
      filteredTableData.saldos_restantes.forEach(item => {
        const accountNumber = item['No. Cuenta']
        if (accountNumber && !uniqueAccounts.has(accountNumber)) {
          uniqueAccounts.set(accountNumber, item)
        }
      })
      
      // Sum the saldo disponible values from unique accounts only
      return Array.from(uniqueAccounts.values()).reduce((sum, item) => sum + (item['Saldo disponible'] || 0), 0)
    })()

    console.log('💰 STATISTICS UPDATE:', {
      totalProgramado,
      saldoDisponibleTotal,
    })

    return {
      totalProgramado,
      saldoDisponibleTotal,
      countRevisar: filteredTableData.facturas_revisar?.length || 0,
      countMenorCuantia: filteredTableData.facturas_menor_cuantia?.length || 0,
      countNoCruzan: filteredTableData.facturas_no_cruzan?.length || 0,
      countFacturasRemover: filteredTableData.facturas_remover?.length || 0,
      countSaldos: filteredTableData.saldos_restantes?.length || 0,
    }
  }, [filteredTableData])

  // Define column structure for facturas tables - memoized to prevent flickering
  const facturaColumnsForDisplay: GridColDef[] = useMemo(() => [
    {
      field: 'No. CONS.',
      headerName: 'No. CONS.',
      width: 60,
      filterable: true,
    },
    {
      field: 'LOTE',
      headerName: 'LOTE',
      width: 60,
      filterable: true,
    },
    {
      field: 'No.',
      headerName: 'No.',
      width: 60,
      filterable: true,
    },
    {
      field: 'NIT',
      headerName: 'NIT',
      width: 120,
      filterable: true,
    },
    {
      field: 'PROVEEDOR',
      headerName: 'PROVEEDOR',
      width: 200,
      filterable: true,
    },
    {
      field: 'TIPO',
      headerName: 'TIPO',
      width: 60,
      filterable: true,
    },
    {
      field: 'CONCEPTO',
      headerName: 'CONCEPTO',
      width: 200,
      filterable: true,
    },
    {
      field: 'FECHA',
      headerName: 'FECHA',
      width: 120,
      type: 'date',
      valueGetter: (value) => value && new Date(value),
    },
    {
      field: 'No. DOC.',
      headerName: 'No. DOC.',
      width: 120,
      filterable: true,
    },
    {
      field: 'SUBTOTAL',
      headerName: 'SUBTOTAL',
      width: 120,
      type: 'number',
      align: 'right',
      renderCell: (params) => formatCurrency(params.value || 0),
    },
    {
      field: 'V/R IVA',
      headerName: 'V/R IVA',
      width: 120,
      type: 'number',
      align: 'right',
      renderCell: (params) => formatCurrency(params.value || 0),
    },
    {
      field: 'PREST.',
      headerName: 'PREST.',
      width: 120,
      type: 'number',
      align: 'right',
      renderCell: (params) => formatCurrency(params.value || 0),
    },
    {
      field: 'V/R COBRO',
      headerName: 'V/R COBRO',
      width: 120,
      type: 'number',
      align: 'right',
      renderCell: (params) => formatCurrency(params.value || 0),
    },
    {
      field: 'DCTO FIN.',
      headerName: 'DCTO FIN.',
      width: 120,
      type: 'number',
      align: 'right',
      renderCell: (params) => formatCurrency(params.value || 0),
    },
    {
      field: 'RET FTE',
      headerName: 'RET FTE',
      width: 120,
      type: 'number',
      align: 'right',
      renderCell: (params) => formatCurrency(params.value || 0),
    },
    {
      field: 'RET IVA',
      headerName: 'RET IVA',
      width: 120,
      type: 'number',
      align: 'right',
      renderCell: (params) => formatCurrency(params.value || 0),
    },
    {
      field: 'RET ICA',
      headerName: 'RET ICA',
      width: 120,
      type: 'number',
      align: 'right',
      renderCell: (params) => formatCurrency(params.value || 0),
    },
    {
      field: 'RETENIDO',
      headerName: 'RETENIDO',
      width: 120,
      type: 'number',
      align: 'right',
      renderCell: (params) => formatCurrency(params.value || 0),
    },
    {
      field: 'L. ANTI',
      headerName: 'L. ANTI',
      width: 120,
      type: 'number',
      align: 'right',
      renderCell: (params) => formatCurrency(params.value || 0),
    },
    {
      field: 'PREST. EMPLEADO',
      headerName: 'PREST. EMPLEADO',
      width: 150,
      type: 'number',
      align: 'right',
      renderCell: (params) => formatCurrency(params.value || 0),
    },
    {
      field: 'DCTO NOM EMP.',
      headerName: 'DCTO NOM EMP.',
      width: 150,
      type: 'number',
      align: 'right',
      renderCell: (params) => formatCurrency(params.value || 0),
    },
    {
      field: 'V/R CHEQUE',
      headerName: 'V/R CHEQUE',
      width: 150,
      type: 'number',
      align: 'right',
      renderCell: (params) => formatCurrency(params.value || 0),
    },
    {
      field: 'CLASIFIC.',
      headerName: 'CLASIFIC.',
      width: 120,
      filterable: true,
      type: 'singleSelect',
      valueOptions: uniqueValues.clasificacion,
    }
  ], [formatCurrency, uniqueValues])

  // Equivalencias columns - memoized to prevent flickering
  const equivalenciasColumns: GridColDef[] = useMemo(() => [
    {
      field: 'Codigo Gerencia',
      headerName: 'Codigo Gerencia',
      width: 200,
      filterable: true
    },
    {
      field: 'Concepto',
      headerName: 'Concepto',
      width: 200,
      filterable: true,
    }
  ], [])

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue)
  }


  // Handle filter changes for global filter sync
  const handleSingleFilterChange = useCallback((filterModel: GridFilterModel) => {
    // Update global filter model for cross-tab synchronization
    setGlobalFilterModel(filterModel)
    console.log(`🔍 Global filter changed`)
  }, [])



  // Handle final confirmation
  const handleConfirmActualizacion = useCallback(() => {
    setConfirmDialogOpen(true)
  }, [])

  const confirmFinalActualizacion = useCallback(async () => {
    setConfirmDialogOpen(false)
    
    try {
      // Get the uploaded files from Flask session via API call
      const sessionResponse = await fetch('/api/get-session-files', {
        method: 'GET',
        credentials: 'include',
      })
      
      if (!sessionResponse.ok) {
        throw new Error('No se pudieron obtener los archivos de la sesión')
      }
      
      const sessionData = await sessionResponse.json()
      
      // Create FormData to send files to the API
      const formData = new FormData()
      
      // Get the main facturas file (include file_type as query param)
      const facturasFileResponse = await fetch(`/api/get-temp-file/${sessionData.facturas_file_id}/facturas`, {
        method: 'GET',
        credentials: 'include',
      })
      
      if (!facturasFileResponse.ok) {
        throw new Error('No se pudo obtener el archivo de facturas')
      }
      
      const facturasBlob = await facturasFileResponse.blob()
      formData.append('input_file', facturasBlob, 'facturas.xlsx')
      
      // Get the optional equivalencias file if it exists (include file_type)
      if (sessionData.equivalencias_file_id) {
        const equivalenciasFileResponse = await fetch(`/api/get-temp-file/${sessionData.equivalencias_file_id}/equivalencias`, {
          method: 'GET',
          credentials: 'include',
        })
        
        if (equivalenciasFileResponse.ok && equivalenciasFileResponse.headers.get('content-length') !== '0') {
          const equivalenciasBlob = await equivalenciasFileResponse.blob()
          // Only append if blob has content
          if (equivalenciasBlob.size > 0) {
            formData.append('config_excel', equivalenciasBlob, 'equivalencias.xlsx')
          } else {
            console.warn('Equivalencias file is empty, skipping')
          }
        } else {
          console.warn('Equivalencias file not available or empty')
        }
      }
      
      // Call the correct API endpoint
      const response = await fetch('/api/v1/actualizar_facturas', {
        method: 'POST',
        body: formData,
      })
      
      if (!response.ok) {
        const errorData = await response.text()
        throw new Error(`Error del servidor: ${errorData}`)
      }
      
      const result = await response.json()
      
      // Handle successful response
      alert(`Actualización completada. ${result.messages || 'Facturas actualizadas correctamente.'}`)
      
      // Optionally redirect to results page or refresh
      window.location.href = '/' // or wherever you want to redirect
      
    } catch (error) {
      console.error('Error:', error)
      alert(`Error al actualizar las facturas: ${error.message}`)
    }
  }, [])


  return (
    <Box sx={{ width: '100%' }}>
      {/* Success Message */}
      {mensaje && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {mensaje}
        </Alert>
      )}


      {/* Tabs */}
      <Paper elevation={1} sx={{ position: 'relative', minHeight: '600px' }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minWidth: 'auto',
              px: 2,
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Facturas
                <Chip size="small" label={filteredTableData.facturas?.length || 0} color="primary" />
              </Box>
            }
            {...a11yProps(0)}
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Equivalencias Conceptos
                <Chip size="small" label={filteredTableData.equivalencias_conceptos?.length || 0} color="success" />
              </Box>
            }
            {...a11yProps(5)}
          />
        </Tabs>

        {/* Tab Panels */}
        <TabPanel value={currentTab} index={0}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Facturas:</strong> Facturas subidas por el usuario, listas para actualizar sus conceptos.
          </Alert>
          <TreasuryDataGrid
            columns={facturaColumnsForDisplay}
            rows={tableData.facturas}
            title="Facturas"
            subtitle={`${tableData.facturas.length} facturas subidas`}
            tableId="facturas"
            showToolbar={true}
            showExport={true}
            showPrint={true}
            showRefresh={true}
            enableStatePersistence={true}
            showColumnCustomization={true}
            filterModel={globalFilterModel}
            onFilterModelChange={handleSingleFilterChange}
            onRefresh={() => window.location.reload()}
            exportOptions={{
              filename: `facturas_${new Date().toISOString().split('T')[0]}`,
              includeHeader: true,
              includeFooter: true,
              includeSummary: true
            }}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>Equivalencias Conceptos:</strong> Este reporte muestra las equivalencias de conceptos
            entre la nomenclatura de Gerencia y la nomenclatura de Docuware.
          </Alert>
          <TreasuryDataGrid
            columns={equivalenciasColumns}
            rows={tableData.equivalencias_conceptos}
            title="Equivalencias de Conceptos"
            subtitle={`${tableData.equivalencias_conceptos.length} equivalencias encontradas`}
            tableId="equivalencias"
            showToolbar={true}
            showExport={true}
            showPrint={true}
            enableStatePersistence={true}
            showColumnCustomization={true}
            filterModel={globalFilterModel}
            onFilterModelChange={handleSingleFilterChange}
            exportOptions={{
              filename: `equivalencias_conceptos_${new Date().toISOString().split('T')[0]}`,
              includeHeader: true,
              includeFooter: true,
              includeSummary: true
            }}
          />
        </TabPanel>
      </Paper>
    
      {/* Floating Action Toolbar */}
      <FloatingActionToolbar
        leftButton={{
          label: 'Volver',
          onClick: () => window.history.back(),
          variant: 'outlined'
        }}
        rightButton={
          currentTab === 0 ? {
            // Tab 0 (Facturas): Show Confirmar button
            label: 'Confirmar Actualización',
            onClick: handleConfirmActualizacion,
            variant: 'contained',
            color: 'success',
            size: 'large',
            icon: <CheckCircleIcon />,
            disabled: false
          } : undefined
        }
        enhanceOnBottom={true}
      />


      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirmar Actualización de Facturas</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de confirmar la actualización de conceptos para {tableData.facturas.length} facturas?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancelar</Button>
          <Button onClick={confirmFinalActualizacion} variant="contained" color="success">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      </Box>
  )
}

// Main component - no longer needs GlobalFilterProvider
export const ActualizarFacturasPreview: React.FC<ActualizarFacturasProps> = (props) => {
  return <ActualizarFacturasInner {...props} />
}

export default ActualizarFacturasPreview